import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { ServiceAreasSection } from "@/components/CrossLinks";

export default function Replacement() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.saskroofingcompany.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://www.saskroofingcompany.com/services",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Roof Replacement",
          item: "https://www.saskroofingcompany.com/services/replacement",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": "https://www.saskroofingcompany.com/services/replacement#service",
      serviceType: "Roof Replacement",
      name: "Roof Replacement",
      description:
        "Complete roof replacement services in Saskatchewan. We tear off old shingles and install durable new roofing systems.",
      url: "https://www.saskroofingcompany.com/services/replacement",
      provider: {
        "@id": "https://www.saskroofingcompany.com/#localbusiness",
        "@type": "RoofingContractor",
        name: "Sask Roofing Company",
        url: "https://www.saskroofingcompany.com/",
        telephone: "+1-639-739-7969",
      },
      areaServed: [
        {
          "@type": "City",
          name: "Saskatoon",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Regina",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Moose Jaw",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Prince Albert",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Swift Current",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Yorkton",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Estevan",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Weyburn",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
      ],
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "CAD",
        lowPrice: "6000",
        highPrice: "15000",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceUrl: "https://www.saskroofingcompany.com/contact",
        servicePhone: {
          "@type": "ContactPoint",
          telephone: "+1-639-739-7969",
          contactType: "customer service",
          areaServed: "CA-SK",
          availableLanguage: "English",
        },
      },
      termsOfService: "https://www.saskroofingcompany.com/about#warranty",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "87",
        bestRating: "5",
        worstRating: "1",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Can you just roof over my old shingles?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "No, we do not recommend roofing over old shingles. A full tear-off is required to inspect the decking for rot and ensure the new roof lays flat and seals properly.",
          },
        },
        {
          "@type": "Question",
          name: "How long does a roof replacement take?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Most residential roof replacements in Saskatchewan are completed in 1 to 2 days, weather permitting.",
          },
        },
        {
          "@type": "Question",
          name: "Will my landscaping be damaged?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "We take extensive precautions, including laying down tarps and protecting delicate plants, to ensure your property looks exactly as it did before we arrived.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Roof Replacement Saskatchewan | New Roof Installation"
        description="Expert roof replacement in Saskatchewan. Get a durable new roof installed by local professionals with honest, fixed pricing and comprehensive warranties."
        schema={schema}
      />

      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/04b10f0a-3e4a-4a6e-a03b-9f26ff276b90.png&output=webp&q=60"
            alt="Roof Replacement"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Roof Replacement Saskatchewan
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              When repairs are no longer enough, we provide complete,
              professional roof replacements built to handle Saskatchewan's
              harshest weather.
            </p>
            <div className="pt-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90"
                asChild
              >
                <Link to="/contact">Get Your Free Replacement Quote</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">
                When You Need a Roof Replacement
              </h2>
              <p className="text-lg text-muted-foreground">
                You need a full roof replacement when your roof is nearing the
                end of its 20-25 year lifespan, has extensive curling or missing
                shingles, widespread granule loss, or if you're experiencing
                multiple leaks in different areas of the home.
              </p>
              <h3 className="text-2xl font-bold pt-4">How It Works</h3>
              <p className="text-lg text-muted-foreground">
                We perform a complete tear-off of your old roofing material down
                to the bare wood deck. We replace any rotted plywood, install
                premium synthetic underlayment, apply ice and water shield to
                critical areas, and install your new roofing system with
                precision.
              </p>
            </div>
            <div className="bg-muted/30 p-8 rounded-xl border">
              <h3 className="text-2xl font-bold mb-6">
                Why It Suits Saskatchewan
              </h3>
              <ul className="space-y-4">
                {[
                  "A full tear-off allows us to fix hidden winter condensation damage in the decking.",
                  "We upgrade your ventilation to prevent future ice dams.",
                  "New architectural shingles offer significantly higher wind resistance for prairie storms.",
                  "A properly installed new roof dramatically improves your home's energy efficiency.",
                ].map((item, i) => (
                  <li key={i} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-20 bg-muted/30">
        <div className="container px-4 max-w-4xl">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold">
              Materials We Use for Roof Replacement
            </h2>
            <p className="text-lg text-muted-foreground">
              A full roof replacement is your opportunity to upgrade your home's
              defense system. We install high-performance architectural asphalt
              shingles and premium standing seam metal roofs. Every replacement
              includes a complete synthetic underlayment system, heavy-duty ice
              and water shield installed past the warm wall line, and upgraded
              ridge ventilation. We only use materials rated for high wind
              uplift (130+ km/h) and severe temperature fluctuations, ensuring
              your new roof performs flawlessly in Saskatchewan.
            </p>
            <div className="mt-8">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/04b10f0a-3e4a-4a6e-a03b-9f26ff276b90.png&output=webp&q=60"
                alt="Roofer installing new underlayment over a bare roof deck"
                className="rounded-xl shadow-md w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Replacement Warning Signs */}
      <section className="py-20 bg-background">
        <div className="container px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            3 Signs You Need a Roof Replacement
          </h2>
          <div className="space-y-6">
            {[
              {
                title: "Age of the Roof",
                desc: "If your asphalt shingle roof is approaching or past 20 years old, the materials have likely degraded to the point where repairs are no longer cost-effective.",
              },
              {
                title: "Widespread Granule Loss",
                desc: "When shingles lose their protective granules, they look 'bald' and are highly susceptible to UV damage and water intrusion.",
              },
              {
                title: "Sagging Roof Deck",
                desc: "A drooping or sagging roofline indicates structural issues or extensive moisture damage in the decking, requiring a full tear-off to fix.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="flex gap-6 bg-muted/30 p-6 rounded-xl border"
              >
                <div className="text-4xl font-bold text-primary/30 shrink-0">
                  0{idx + 1}
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost & Process */}
      <section className="py-20 bg-muted/50">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">What It Costs</h2>
              <div className="bg-white p-6 rounded-xl border border-primary/20 shadow-sm mb-6">
                <div className="text-sm font-bold text-primary uppercase tracking-wider mb-2">
                  Estimated Investment
                </div>
                <div className="text-3xl font-bold text-foreground">
                  $6,000 - $15,000+
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  *Cost depends on home size, pitch, and material choice.
                </p>
              </div>
              <p className="text-lg text-muted-foreground">
                The cost of a roof replacement in Saskatchewan generally ranges
                from $6,000 to $15,000 for standard homes, depending on the
                square footage, roof pitch, and whether you choose{" "}
                <Link to="/services/asphalt" className="underline text-primary">
                  asphalt shingles
                </Link>{" "}
                or{" "}
                <Link to="/services/metal" className="underline text-primary">
                  metal roofing
                </Link>
                . We provide transparent, fixed-price contracts and offer{" "}
                <Link
                  to="/financing"
                  className="underline text-primary font-bold"
                >
                  flexible financing options
                </Link>{" "}
                to help manage the cost. For an in-depth look, read our{" "}
                <Link
                  to="/roofing-cost-guide-saskatchewan"
                  className="text-accent hover:underline font-bold"
                >
                  Saskatchewan Roofing Cost Guide
                </Link>
                .
              </p>
              <div className="mt-8 rounded-2xl overflow-hidden shadow-2xl h-64">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/04b10f0a-3e4a-4a6e-a03b-9f26ff276b90.png&output=webp&q=60"
                  alt="Brand new architectural shingle roof"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">Our Process</h2>
              <div className="space-y-6 mt-8">
                {[
                  {
                    title: "Detailed Estimate",
                    desc: "Accurate measurements and clear material options.",
                  },
                  {
                    title: "Property Protection",
                    desc: "Tarps and plywood to protect your landscaping.",
                  },
                  {
                    title: "Complete Tear-off",
                    desc: "Removing all old layers to inspect the deck.",
                  },
                  {
                    title: "System Installation",
                    desc: "Installing underlayment, flashing, and the new roof.",
                  },
                  {
                    title: "Thorough Cleanup",
                    desc: "Multiple magnetic sweeps to ensure no nails are left behind.",
                  },
                ].map((step, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0">
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{step.title}</h3>
                      <p className="text-muted-foreground mt-1">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <ServiceAreasSection
        service="Roof Replacement"
        serviceSlug="replacement"
      />

      <section className="py-20 bg-background">
        <div className="container px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Roof Replacement FAQ
          </h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-lg font-medium text-left">
                Can you just roof over my old shingles?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                No, we do not recommend roofing over old shingles. A full
                tear-off is required to inspect the decking for rot and ensure
                the new roof lays flat and seals properly.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-lg font-medium text-left">
                How long does a roof replacement take?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Most residential roof replacements in Saskatchewan are completed
                in 1 to 2 days, weather permitting.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-lg font-medium text-left">
                Will my landscaping be damaged?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                We take extensive precautions, including laying down tarps and
                protecting delicate plants, to ensure your property looks
                exactly as it did before we arrived.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-3xl space-y-8">
          <h2 className="text-3xl font-bold">Ready for a Roof That Lasts?</h2>
          <p className="text-xl text-primary-foreground/80">
            We serve Saskatoon, Regina, Moose Jaw, Prince Albert, Swift Current,
            Yorkton, Estevan, and Weyburn. Most roof replacement projects take
            just 1-2 days. Book a free inspection today. Unsure if you need a
            full replacement? Start with a{" "}
            <Link
              to="/services/inspections"
              className="underline hover:text-accent"
            >
              roof inspection
            </Link>
            .
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              asChild
            >
              <Link to="/contact">Get Your Free Estimate</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-white/10"
              asChild
            >
              <Link to="/calculator">Try Our Cost Calculator</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
